import { Minimize } from '@app/components/icons/minimize';
import { Tooltip } from '@app/components/tooltip';
import { CreatorLabMenuContentType } from '@app/routes/content/analytics/events';
import { useAppDispatch, useAppSelector } from '@app/store/store';
import { Tab as HeadlessTab } from '@headlessui/react';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { EditorTabs } from '../../domain';
import { useRecordingAnalytics } from '../../hooks';
import { selectContextMenuTabId } from '../../selector';
import { ContextMenuTabGroup } from './context-menu-tab';

const mapTabIdToMenuContentType = (
  tabId: EditorTabs,
): CreatorLabMenuContentType | null => {
  const mapping: Partial<Record<EditorTabs, CreatorLabMenuContentType>> = {
    'content-composer': 'content-composer',
    'content-social-post': 'social_media',
    'content-blog-post': 'blog',
    'content-email': 'email',
  };
  return mapping[tabId] || null;
};

export type ContextMenuProps = {
  groups: ContextMenuTabGroup[];
  className?: string;
  /** When true, the panel can be collapsed to show only the icon strip. Default: false. */
  isCollapsible?: boolean;
};

export const ContextMenu = ({
  groups,
  className,
  isCollapsible = false,
}: ContextMenuProps) => {
  const dispatch = useAppDispatch();
  const selectedTabId = useAppSelector(selectContextMenuTabId);
  const { trackRecordingEvent } = useRecordingAnalytics();
  const { t } = useTranslation();

  const allTabs = useMemo(() => {
    return groups.flatMap(group => group.tabs.filter(tab => !tab.hidden));
  }, [groups]);

  // isCollapsed is always false when isCollapsible is false (old editor).
  const isCollapsed = isCollapsible && !selectedTabId;

  const selectedTabWidthClassName = useMemo(() => {
    const selectedTab = allTabs.find(tab => tab.id === selectedTabId);
    return selectedTab?.widthClassName || 'w-[26rem]';
  }, [allTabs, selectedTabId]);

  // Measure the icon strip width for a smooth collapse/expand CSS transition.
  // CSS cannot interpolate to/from `auto`, so we need a concrete px value.
  const stripRef = useRef<HTMLDivElement>(null);
  const containerStyle =
    isCollapsed && stripRef.current
      ? { width: stripRef.current.offsetWidth }
      : undefined;

  // Old editor: always auto-select first tab when none is selected.
  // EditorV2: auto-select once on mount (starts expanded), then respect user collapse.
  const hasAutoInitialized = useRef(false);

  useEffect(() => {
    if (!selectedTabId && allTabs.length > 0) {
      if (!isCollapsible || !hasAutoInitialized.current) {
        hasAutoInitialized.current = true;
        dispatch({
          type: '[recording] context menu tab changed',
          payload: { tabId: allTabs[0].id },
        });
      }
    }
  }, [isCollapsible, selectedTabId, allTabs, dispatch]);

  // If the tab list changes (e.g. edit vs default preset) and Redux still holds an
  // id that no longer exists, sync to the first available tab so the strip highlights correctly.
  useEffect(() => {
    if (
      !selectedTabId ||
      allTabs.length === 0 ||
      allTabs.some(tab => tab.id === selectedTabId)
    ) {
      return;
    }
    dispatch({
      type: '[recording] context menu tab changed',
      payload: { tabId: allTabs[0].id },
    });
  }, [selectedTabId, allTabs, dispatch]);

  const selectedIndex = useMemo(() => {
    if (!selectedTabId) {
      return allTabs.length > 0 ? 0 : undefined;
    }
    const index = allTabs.findIndex(tab => tab.id === selectedTabId);
    return index >= 0 ? index : 0;
  }, [selectedTabId, allTabs]);

  const handleTabClick = useCallback(
    (tabId: EditorTabs) => {
      if (isCollapsible && selectedTabId === tabId) {
        dispatch({
          type: '[recording] context menu tab changed',
          payload: { tabId: null },
        });
        return;
      }

      dispatch({
        type: '[recording] context menu tab changed',
        payload: { tabId },
      });
      trackRecordingEvent('ch_recording_editor_tab_switched', {
        tabName: tabId,
      });

      if (tabId === 'video-appearance') {
        trackRecordingEvent('ch_creator_lab_appearance_tab_clicked');
      }

      const menuContentType = mapTabIdToMenuContentType(tabId);
      if (menuContentType) {
        trackRecordingEvent('creator_lab_tab_switched', {
          tab: menuContentType,
        });
      }
    },
    [dispatch, isCollapsible, selectedTabId, trackRecordingEvent],
  );

  const handleCollapse = useCallback(() => {
    dispatch({
      type: '[recording] context menu tab changed',
      payload: { tabId: null },
    });
  }, [dispatch]);

  return (
    <HeadlessTab.Group
      selectedIndex={selectedIndex}
      onChange={(index: number) => {
        // Old editor uses onChange as the sole click handler.
        // EditorV2 uses onClick on each tab instead (see below).
        if (!isCollapsible) {
          const tab = allTabs[index];
          if (tab) {
            handleTabClick(tab.id);
          }
        }
      }}
      as="div"
      style={containerStyle}
      className={cx(
        'flex flex-row h-full transition-all duration-300 overflow-hidden',
        !isCollapsed && selectedTabWidthClassName,
        className,
      )}
    >
      <div
        ref={stripRef}
        className="flex flex-col self-stretch shrink-0 px-3 py-6 border-l border-r border-spring-neutral-b0/10"
      >
        <HeadlessTab.List className="flex flex-col gap-3">
          {groups.map((group, groupIndex) => (
            <React.Fragment key={group.id}>
              <div className="flex flex-col items-center gap-3 px-0 py-0">
                <span className="text-[10px] font-medium leading-none text-spring-neutral-b4 uppercase tracking-normal">
                  {t(`content-recording.content-menu.groups.${group.id}`)}
                </span>
                <div className="flex flex-col gap-3">
                  {group.tabs
                    .filter(tab => !tab.hidden)
                    .map(tab => {
                      const isActive = selectedTabId === tab.id;
                      const isDisabled = tab.disabled;

                      return (
                        <Tooltip
                          key={tab.id}
                          tooltip={t(
                            `content-recording.content-menu.tabs.${tab.id}`,
                          )}
                          placement="left-center"
                        >
                          <HeadlessTab
                            as="button"
                            type="button"
                            disabled={isDisabled}
                            aria-label={tab.label || tab.id}
                            {...(isActive && { 'aria-selected': 'true' })}
                            {...(isCollapsible && {
                              onClick: () => handleTabClick(tab.id),
                            })}
                            className={cx(
                              'flex flex-col items-center justify-center relative shrink-0 w-9 h-9 p-2.5 rounded-[8px] transition-colors duration-200 focus:outline-none',
                              {
                                'bg-spring-primary': isActive,
                                'bg-transparent': !isActive,
                                'opacity-50 cursor-not-allowed': isDisabled,
                                'cursor-pointer hover:bg-spring-neutral-b0/10':
                                  !isActive && !isDisabled,
                              },
                            )}
                          >
                            <div className="relative shrink-0 size-5 text-white">
                              {tab.icon}
                              {tab.showNotificationBadge && !isActive && (
                                <span className="absolute -top-1.5 -right-1.5 size-2 bg-spring-cobranding rounded-full animate-pulse" />
                              )}
                            </div>
                            {tab.label && (
                              <span
                                className={cx('text-xs mt-1', {
                                  'text-white': isActive,
                                  'text-spring-neutral-b2': !isActive,
                                })}
                              >
                                {tab.label}
                              </span>
                            )}
                          </HeadlessTab>
                        </Tooltip>
                      );
                    })}
                </div>
              </div>
              {groupIndex < groups.length - 1 && (
                <div className="flex flex-col items-center justify-center gap-2.5 self-stretch py-0">
                  <div className="size-1 bg-spring-neutral-b5 rounded-full" />
                </div>
              )}
            </React.Fragment>
          ))}
        </HeadlessTab.List>

        {isCollapsible && !isCollapsed && (
          <div className="flex-grow flex items-end justify-center">
            <Tooltip tooltip="Collapse panel" placement="left-center">
              <button
                type="button"
                aria-label="Collapse panel"
                onClick={handleCollapse}
                className="flex items-center justify-center shrink-0 w-9 h-9 bg-transparent cursor-pointer hover:bg-spring-neutral-b0/10 transition-colors duration-200 focus:outline-none"
              >
                <Minimize className="size-5 text-white" />
              </button>
            </Tooltip>
          </div>
        )}
      </div>

      {/* Panels use display:none when collapsed to preserve scroll position and ephemeral state. */}
      <HeadlessTab.Panels
        className={cx('flex-1 h-full overflow-hidden bg-spring-neutral-b5/25', {
          hidden: isCollapsed,
        })}
      >
        {allTabs.map(tab => (
          <HeadlessTab.Panel
            key={tab.id}
            className="h-full overflow-hidden py-4"
          >
            {tab.panel}
          </HeadlessTab.Panel>
        ))}
      </HeadlessTab.Panels>
    </HeadlessTab.Group>
  );
};
